// One route (TX-US-0030, TX-US-0032): its fields as inputs, its figures beside them. A cell is
// saved when it is left or Enter is pressed - the unit when it is picked - and only if it changed
// and the API would take it; the figures are whatever the save answers with. A route someone else
// has deleted answers 404: the list reloads. Beside each figure, a question mark opens the formula
// that reached it. The deal details and the route's name wrap onto as many lines as they need;
// Enter still saves, so neither holds a line break.

import { useRef, useState } from 'react';

import { saveFleetRoute } from '../../api.js';
import Icon from '../../ui/Icon.jsx';
import FormulaTip from '../FormulaTip.jsx';
import { day } from '../format.js';
import {
  cycleTex, loadsPerDayTex, tripsPerTruckTex, tripsRequiredTex, trucksRequiredTex,
} from './requirementTex.js';
import {
  DAY_FIELDS, DEAL_FIELDS, NAME_FIELD, PLACE_FIELDS, UNIT_FIELD, UNITS, VOLUME_FIELDS, changed,
  draftOf, fieldsOf, figure, invalidFields,
} from './routeFields.js';

const leaveOnEnter = (event) => {
  if (event.key === 'Enter') event.currentTarget.blur();
};

export default function RouteRow({ line, daysPerMonth, onFleet, onGone, onDelete }) {
  const { route, requirement } = line;
  const [draft, setDraft] = useState(() => draftOf(route));
  const [error, setError] = useState(null);
  const invalid = invalidFields(draft);
  // Tabbing through cells starts a save a cell; only the answer to the last one is drawn, so an
  // earlier answer arriving late never puts older figures back.
  const lastSave = useRef(0);

  const commit = async (edited = draft) => {
    if (invalidFields(edited).length || !changed(route, edited)) return;
    setError(null);
    lastSave.current += 1;
    const save = lastSave.current;
    try {
      const fleet = await saveFleetRoute(route.route_id, fieldsOf(edited));
      if (save === lastSave.current) onFleet(fleet);
    } catch (err) {
      if (err.status === 404) onGone();
      else setError(err.message);
    }
  };

  const editing = ({ key, label }) => ({
    'aria-label': label,
    'aria-invalid': invalid.includes(key) || undefined,
    value: draft[key],
    onChange: (event) => setDraft({ ...draft, [key]: event.target.value }),
    onBlur: () => commit(),
    onKeyDown: leaveOnEnter,
  });
  const input = (field, numeric) => (
    <input inputMode={numeric ? 'decimal' : undefined} {...editing(field)} />
  );
  const wrapping = (field) => <textarea rows={1} {...editing(field)} />;
  const cell = (field, numeric) => (
    <td key={field.key} className={numeric ? 'num' : undefined}>{input(field, numeric)}</td>
  );
  const [product, dealDetails] = DEAL_FIELDS;

  // A select has no moment of being left with a change still unsaved: picking is the change.
  const onUnit = (event) => {
    const edited = { ...draft, unit: event.target.value };
    setDraft(edited);
    commit(edited);
  };

  // A figure and the formula that reached it; a figure that is not there has no formula.
  const derived = (caption, value, places, tex, className = '') => (
    <td className={`num derived ${className}`}>
      <span className="fleet-figure">{figure(value, places)}</span>
      {value !== null && <FormulaTip caption={caption} title={route.name} tex={tex()} />}
    </td>
  );

  return (
    <tr data-route-id={route.route_id}>
      {cell(product, false)}
      <td>{wrapping(dealDetails)}</td>
      {/* Under the name: who last saved the route, or why the last save was refused. */}
      <td>
        {wrapping(NAME_FIELD)}
        {error
          ? <span className="fleet-saved error" role="alert">{error}</span>
          : (
            <span className="fleet-saved muted">
              {route.updated_by.split('@')[0]}, {day(route.updated_at.slice(0, 10))}
            </span>
          )}
      </td>
      {PLACE_FIELDS.map((field) => cell(field, false))}
      {DAY_FIELDS.map((field) => cell(field, true))}
      {derived('Total cycle', requirement.cycle_days, 2, () => cycleTex(route, requirement))}
      {VOLUME_FIELDS.map((field) => cell(field, true))}
      <td>
        <select aria-label={UNIT_FIELD.label} value={draft.unit} onChange={onUnit}>
          {UNITS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
        </select>
      </td>
      {derived('Trips per truck', requirement.trips_per_truck, 2,
        () => tripsPerTruckTex(requirement, daysPerMonth))}
      {derived('Trips required', requirement.trips_required, 0,
        () => tripsRequiredTex(route, requirement))}
      {derived('Trucks required', requirement.trucks_required, 0,
        () => trucksRequiredTex(requirement, daysPerMonth), 'fleet-trucks')}
      {derived('Loads a day', requirement.loads_per_day, 2,
        () => loadsPerDayTex(requirement, daysPerMonth))}
      <td className="row-actions">
        <button type="button" className="icon-button quiet" onClick={() => onDelete(route)}
                aria-label={`Delete ${route.name}`} title="Delete">
          <Icon name="trash" />
        </button>
      </td>
    </tr>
  );
}
